<template>
  <Collapse
    class="ai-search-banner"
    :class="{
      'ai-search-banner--pc': deviceType === 'PC'
    }"
    v-model="active"
    v-bind="$attrs"
    accordion
    :disabled="status < 3"
  >
    <CollapseTrigger
      name="ai-search-banner"
    >
      <PageContainer
        class="ai-search-banner__header"
        as-child
      >
        <img
          class="ai-search-banner__logo"
          src="@/assets/colorIcon/ai-star.svg"
          alt="ai"
        >
        <p class="ai-search-banner__title">
          Deep Search
        </p>
        <Swiper
          class="ai-search-banner__swiper"
          :direction="'vertical'"
          @swiper="(swiper) => swiperRef = swiper"
        >
          <template
            v-for="(text, index) of STATUS_MAP"
            :key="index"
          >
            <SwiperSlide
              class="ai-search-banner__searching"
              v-if="index < 3"
            >
              <img
                src="@/assets/colorIcon/searching.svg"
                alt="searching"
              >
              {{ text }} &nbsp;
              <div class="ai-search-banner-thinking">
                <span
                  class="ai-search-banner-thinking__dot"
                  v-for="i of 3"
                />
              </div>
            </SwiperSlide>
            <SwiperSlide
              class="ai-search-banner__search"
              v-else
            >
              <img
                src="@/assets/colorIcon/ai-search.svg"
                alt="search"
              >
              {{ text }}

              <div
                class="ai-search-banner__arrow"
              >
                <Icon
                  v-if="!active"
                  name="arrow-right-l-2"
                />
                <Icon
                  v-else
                  class="ai-search-banner__close"
                  name="close-l"
                />
              </div>
            </SwiperSlide>
          </template>
        </Swiper>
      </PageContainer>
    </CollapseTrigger>
    <CollapseContent
      name="ai-search-banner"
      class="ai-search-banner__wrapper"
    >
      <PageContainer
        class="ai-search-banner__content"
        as-child
      >
        <slot />
      </PageContainer>
    </CollapseContent>
  </Collapse>
  <Transition name="popup-overlay">
    <div
      v-if="show"
      class="ai-search-banner__mask"
      :style="{ top: maskTop }"
      @click="active = null"
    />
  </Transition>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Collapse, CollapseTrigger, CollapseContent } from '@/components/Collapse'
import { Icon } from '@/components/Icon'
import { deviceType } from '@/utils/constants'
import { PageContainer } from '@/components/PageLayout'

const props = defineProps({
  /**
   * 请求状态
   * 0 -> Keyword analysising ...
   * 1 -> Organizing related items ...
   * 2 -> Generating, please wait ...
   * 3 -> Search Successfully
   */
  status: {
    type: Number,
    default: 0
  }
})

const STATUS_MAP = [
  'Keyword analysising',
  'Organizing related items',
  'Generating, please wait',
  'Search Successfully'
]

const active = ref(null)
const show = computed(() => active.value != null)
const maskTop = ref(0)
const swiperRef = ref(null)

watch(() => [props.status, swiperRef.value], ([status]) => {
  if (swiperRef.value == null) return
  swiperRef.value.slideTo(status)
})

onMounted(() => {
  const banner = document.querySelector('.ai-search-banner')
  if (banner) maskTop.value = `${banner.offsetTop}px`

  if (swiperRef.value == null) return
  swiperRef.value.slideTo(props.status, 0)
})
</script>

<style lang="less" scoped>
.ai-search-banner {
  z-index: 99;
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  overflow: hidden;
  background-color: #052784;

  @media (min-width: 1024px) {
    top: 0;
    border-radius: unset;
    background: linear-gradient(90deg, #EFF4FD 0%, #DFE8FB 100%);
  }

  &__header {
    display: flex;
    align-items: center;
    padding-top: 16px;
    padding-bottom: 16px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background: linear-gradient(90deg, #EFF4FD 0%, #DFE8FB 100%);
  }

  &__logo {
    width: 24px;
    height: 24px;
  }

  &__title {
    padding-top: 3px;
    font-family: mon-700;
    font-size: 16px;
  }

  &__swiper {
    height: 20px;
    margin-right: 0;
  }

  &__searching, &__search {
    margin-left: auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-family: mon-600;
    font-size: 13px;
  }

  &__searching {
    > img {
      width: 24px;
      height: 24px;
    }
  }

  &-thinking {
    display: flex;
    gap: 2px;

    &__dot {
      display: block;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: #232323;
      animation: fade 1.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);

      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  }

  @keyframes fade {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.2;
      transform: scale(0.85);
    }
  }

  &__search {
    justify-content: flex-end;
    gap: 6px;

    > img {
      width: 16px;
      height: 16px;
    }
  }

  &__arrow {
    margin-left: 2px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    background: rgba(176, 199, 245, 0.5);
    border-radius: 50%;
    font-size: 12px;
  }

  &__close {
    font-size: 10px;
  }

  &__wrapper {
    background-color: #FFFFFF;
  }

  &__content {
    padding-top: 16px;
    padding-bottom: 16px;
  }

  &__mask {
    z-index: 90;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.64);
    backdrop-filter: blur(8px)
  }
}

.popup-overlay-enter-active,
.popup-overlay-leave-active {
  transition: opacity 0.3s ease;
  transition-delay: 0.1s;
}
.popup-overlay-enter-from,
.popup-overlay-leave-to {
  opacity: 0;
}
</style>
